import Handlebars from '@sparta.global.vendors/handlebars';
import { button } from '@sparta.global.helpers/button-helper/dist/es6.js';
import { requiredAttrsCheck, removeReservedAttrs, combineAttrs, buildAttrsString } from '@sparta.global.helpers/utils-helper/dist/es6.js';

/**
 * Array of input aria-describedby options
 * @description Order should be read as: label | hint text | help layer content if available | validation message if available
 * @param {Object} id input ID to create unique connections for whole input component
 */
function getInputDescribedBy(id, hintText) {
  let inputHintText = hintText ? id + '-hint-text' : null;
  let inputErrorId = id + '_errorMessage';
  let inputDescribedBy = {
    'inputErrorId': inputErrorId,
    'inputHintID': inputHintText
  };
  let inputDescribedByString = '';
  Object.keys(inputDescribedBy).forEach(key => {
    let describedbyValue = inputDescribedBy[key];
    if (describedbyValue != null) inputDescribedByString += `${describedbyValue} `;
  });
  return inputDescribedByString;
}

// The module to be exported
function select(data) {
  const requiredAttrs = ['id'];
  const reservedAttrs = ['type', 'data-mask', 'data-gis-mask'];
  const combinableAttrs = {
    class: 'spa-input-select',
    value: '',
    'data-val': ''
  };
  const validationRules = data.validationRules;
  let attrsStr;
  let optionsString = '';
  const helpToggle = data.helpToggle ? data.helpToggle : data.label.text;
  if (!requiredAttrsCheck(data.select.attrs, requiredAttrs).pass) {
    /*serverSideOnly*/
    throw new Error("You are missing the required attribute " + requiredAttrsCheck(data.select.attrs, requiredAttrs).missingField);
    /*serverSideOnly*/
  }
  if (!data.select.attrs.autocomplete) data.select.attrs.autocomplete = 'off';
  removeReservedAttrs(data.select.attrs, reservedAttrs);
  combineAttrs(data.select.attrs, combinableAttrs);
  attrsStr = buildAttrsString(data.select.attrs);
  if (!data.select.attrs.noEmptyOption) {
    optionsString = '<option value="">&#160;</option>';
  }

  /**
   * Creates dropdown HTML.
   * @param {object} data Option object to create dropdown option element.
   * @returns 
   */
  function createOption(data) {
    return `
      <option
        value="${data.value}"
        ${data.disabled ? 'disabled' : ''}
        ${data.selected ? 'selected' : ''}
        ${data.hidden ? 'hidden' : ''}
      >${data.text}</option>
    `;
  }

  /**
   * Creates optgroup HTML for dropdown.
   * @param {string} label Create label for opt group.
   * @param {array} object Data to create optgroup options.
   * @returns 
   */
  function createOptGroup(label, object) {
    const optGroup = object[label];

    // If already created, return blank string.
    if (optGroup[0].groupCreated === true) {
      return '';
    }

    // Create option markup.
    let optGroupOptionString = '';
    optGroup.forEach(option => {
      optGroupOptionString = `${optGroupOptionString}${createOption(option)}`;
    });

    // Prevents duplication of optgroup.
    optGroup[0].groupCreated = true;

    // Return opgroup.
    return `
      <optgroup
        label="${label}"
      >${optGroupOptionString}</optgroup>
    `;
  }

  // Loop through options and search for any optgroups
  const getOptions = data.select.options;
  const optGroups = getOptions.reduce((r, a) => {
    if (a.optGroup) {
      r[a.optGroup] = r[a.optGroup] || [];
      r[a.optGroup].push(a);
    }
    return r;
  }, Object.create(null));

  // Loop through options and create HTML
  getOptions.forEach(optionObj => {
    // Create optGroup
    if (optionObj.optGroup) {
      optionsString = `${optionsString}${createOptGroup(optionObj.optGroup, optGroups)}`;
    }
    // Create standard options
    else {
      optionsString = `${optionsString}${createOption(optionObj)}`;
    }
  });
  var sSelectContainer = "<div class='spa-input spa-input-box spa-dropdown spa-input--" + (data.skin ? data.skin : "sparta2") + (data.containerClasses ? " " + data.containerClasses.join(" ") : "") + (data.select.attrs["data-help-body"] ? data.select.attrs['data-help-link'] ? " has-help-link" : " has-help" : "") + "'>";
  const inputLabelRequiredSpan = required => {
    // if required show asterisk
    if (!required) {
      return '';
    }
    const asteriskMark = `<span class="spa-input-label--asterisk">&nbsp;*</span>`;
    return asteriskMark;
  };
  var sSelectLabel = "<label class='spa-input-label' for='" + data.select.attrs.id + "'>" + data.label.text + inputLabelRequiredSpan(data.required, data.asterisk) + "<span class='ada-hidden'>" + (data.label.adaText ? data.label.adaText : "") + "</span>" + "</label>";
  let inputDescribedByString = getInputDescribedBy(data.select.attrs.id, data.hintText);
  let ariaDescribedBy = inputDescribedByString ? ` aria-describedby="${inputDescribedByString}"` : '';
  const sSelectField = `
    <select
      ${attrsStr}
      ${data.vueOptionsStr ? data.vueOptionsStr : ''}
      ${validationRules ? `data-validation-rules="${JSON.stringify(validationRules).replace(/"/g, '&#34;')}"` : ''}
      ${data.required ? 'required aria-required="true"' : ''}
      ${data.required && typeof data.required === 'string' ? `data-required-error-message="${data.required}"` : ''}
      ${data.disabled ? 'disabled aria-disabled="true"' : ''}
      ${ariaDescribedBy ? ariaDescribedBy : ''}
    >
      ${optionsString}
    </select>
  `;
  var sSelectHint = `<p class='spa-input-hint' id="${data.select.attrs.id}-hint-text">` + (data.hintText ? data.hintText : "") + "</p>";
  const sSelectError = `<div class='spa-input-error-message spa-icon--error'
    id='${data.select.attrs.id}_errorMessage'
    aria-hidden='true'></div>`;
  if (data.select.attrs['data-help-body']) {
    // Add a help pop-up trigger, either a question mark or some link text.
    const triggerText = data.select.attrs['data-help-link'] || '';
    const triggerClass = triggerText ? 'toggle-trigger-link' : 'toggle-trigger svg-button';
    let sSelectHelpTrigger = button({
      content: triggerText || '&#160;',
      classes: `${triggerClass} spa-btn--link`,
      attributes: {
        id: `help-open-${data.select.attrs.id}`,
        "aria-label": helpToggle
      }
    });
    if (!triggerText) {
      sSelectHelpTrigger = `<span class="toggle-trigger-box">${sSelectHelpTrigger}</span>`;
    }
    var sSelectHelpContainer = "<div class='input-help' id='helpLayer_" + data.select.attrs.id + "' role='tooltip' aria-hidden='true' >";
    const sSelectHelpClose = button({
      content: '&#160;',
      classes: 'close-trigger svg-button',
      attributes: {
        id: `help-close-${data.select.attrs.id}`,
        "aria-label": helpToggle
      }
    });
    var sSelectHelpBody = "<div class='body'>" + data.select.attrs["data-help-body"] + "</div>";
    if (data.select.attrs["data-help-head"]) {
      // New Helix Requirement
      // ---------------------------
      // sSelectHelpBody = "<div class='head' data-font='cnx-bold'>" +
      sSelectHelpBody = "<div class='head'>" + data.select.attrs["data-help-head"] + "</div>" + sSelectHelpBody;
      // ---------------------------
    }
    sSelectHelpContainer += sSelectHelpClose + sSelectHelpBody + "</div>";
    sSelectContainer += sSelectLabel + sSelectField + sSelectHelpTrigger + sSelectHint + sSelectHelpContainer + sSelectError + "</div>";
  } else {
    sSelectContainer += sSelectLabel + sSelectField + sSelectHint + sSelectError + "</div>";
  }
  return new Handlebars.SafeString(sSelectContainer);
}

// Export helpers

export { select };
